import { Center, Loader } from '@mantine/core';
import { isCancelled } from '@reditelji/shared';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useExternalGroups, useExternalGroupMutations } from '../api/externalGroups';
import { useEvent } from '../api/hooks';
import { useAllShifts, usePosts } from '../api/posts';
import { useTags } from '../api/tags';
import { useSession } from '../api/session';
import { useTriage, useVolunteerMutations, useVolunteers } from '../api/volunteers';
import { rollupFor } from './derive';
import { liveFilter } from './tagLogic';
import { EmptyState } from './EmptyState';
import { ImportModal, TriageModal } from './modals';
import { PersonDetail, type PersonTab } from './PersonDetail';
import { Sidebar, type QuickFilter } from './Sidebar';
import { TeamDetail } from './TeamDetail';
import { TeamsSidebar } from './TeamsSidebar';
import type { RosterView } from './ViewSwitch';
import { TagManager } from './TagBits';

/**
 * Prostovoljci — master–detail: 306px people rail on the
 * left, full profile (Profil / Komunikacija / Zgodovina) on the right.
 */
export function VolunteersPage() {
  const volunteers = useVolunteers();
  const tags = useTags('volunteer');
  const triage = useTriage();
  const shifts = useAllShifts();
  const posts = usePosts();
  const event = useEvent();
  const groups = useExternalGroups();
  const mut = useVolunteerMutations();
  const teamMut = useExternalGroupMutations();
  const { can } = useSession();
  const canPlan = can('plan:write');
  const canComms = can('comms:read');

  const [search, setSearch] = useState('');
  // Its own box: the two lists hold different things, and carrying a person's
  // name across would empty the teams rail while the switch still counted them.
  const [teamSearch, setTeamSearch] = useState('');
  const [active, setActive] = useState<Set<QuickFilter>>(new Set());
  const [tagFilterIds, setTagFilter] = useState<string[]>([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [tagMgrOpen, setTagMgrOpen] = useState(false);
  const [modal, setModal] = useState<'none' | 'import' | 'triage'>('none');

  // Selection + tab live in the URL (deep links from Pregled: ?volunteer&tab=sporocila)
  const [params, setParams] = useSearchParams();
  // Which of the screen's two lists is showing (SPEC §1.1J). In the URL, not in
  // state, so the board and Pregled can link straight to a team — and anything
  // unrecognised falls back to the roster, keeping every older link working.
  const view: RosterView = params.get('view') === 'ekipe' ? 'ekipe' : 'ljudje';
  const groupId = params.get('ekipa');
  const setView = (v: RosterView) =>
    setParams(v === 'ekipe' ? { view: 'ekipe' } : {}, { replace: true });
  const openTeam = (id: string | null) =>
    setParams(id ? { view: 'ekipe', ekipa: id } : { view: 'ekipe' }, { replace: true });
  const personId = params.get('volunteer');
  const rawTab = params.get('tab');
  const tab: PersonTab =
    rawTab === 'sporocila' || rawTab === 'komunikacija'
      ? 'komunikacija'
      : rawTab === 'zgodovina'
        ? 'zgodovina'
        : rawTab === 'organizacija'
          ? 'organizacija'
          : 'profil';
  const openPerson = (id: string | null, t: PersonTab = 'profil') => {
    setParams(id ? (t === 'profil' ? { volunteer: id } : { volunteer: id, tab: t }) : {}, {
      replace: true,
    });
  };

  const days = event.data?.days ?? [];
  const postById = useMemo(() => new Map((posts.data ?? []).map((p) => [p.id, p])), [posts.data]);
  const tagById = useMemo(() => new Map((tags.data ?? []).map((t) => [t.id, t])), [tags.data]);

  const allRows = useMemo(
    () =>
      (volunteers.data ?? []).map((v) => ({
        v,
        r: rollupFor(v, shifts.data ?? [], days, postById),
      })),
    [volunteers.data, shifts.data, days, postById],
  );
  // Somebody who is not coming leaves the list, its filters and its counts for
  // a section of their own (SPEC §1.1K); their card still opens from there.
  const rows = useMemo(() => allRows.filter(({ v }) => !isCancelled(v)), [allRows]);
  const outRows = useMemo(() => allRows.filter(({ v }) => isCancelled(v)), [allRows]);

  // A tag deleted while it filters the list must not keep the list empty.
  const tagFilter = useMemo(() => liveFilter(tagFilterIds, tagById), [tagFilterIds, tagById]);

  const tagUsage = useMemo(() => {
    const m = new Map<string, number>();
    for (const { v } of rows) for (const id of v.tagIds) m.set(id, (m.get(id) ?? 0) + 1);
    return m;
  }, [rows]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(({ v, r }) => {
      if (q) {
        const tagText = v.tagIds.map((id) => tagById.get(id)?.label ?? '').join(' ');
        const hay = `${v.name} ${v.phone} ${v.email ?? ''} ${v.residence} ${tagText}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (tagFilter.length && !tagFilter.every((id) => v.tagIds.includes(id))) return false;
      if (active.has('free') && r.shifts.length > 0) return false;
      if (active.has('clash') && !r.clash) return false;
      // Not drawn without comms:read, so it must not filter either — a login
      // whose role changed while it was on would have no chip to switch it off.
      if (canComms && active.has('stale') && !r.noReply) return false;
      return true;
    });
  }, [rows, search, tagFilter, active, tagById, canComms]);

  const outShown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return outRows;
    // The same fields the main list searches, tags included.
    return outRows.filter(({ v }) => {
      const tagText = v.tagIds.map((id) => tagById.get(id)?.label ?? '').join(' ');
      return `${v.name} ${v.phone} ${v.email ?? ''} ${v.residence} ${tagText}`.toLowerCase().includes(q);
    });
  }, [outRows, search, tagById]);

  const current = allRows.find((x) => x.v.id === personId) ?? null;

  // Same free-name search as newPerson below: two teams sharing a name would
  // produce two sets of identically labelled people (SPEC §1.1J), which is
  // indistinguishable on the board and in the assignee picker.
  const newTeam = () => {
    const names = new Set((groups.data ?? []).map((g) => g.name));
    let n = 1;
    while (names.has(`Nova ekipa ${n}`)) n += 1;
    teamMut.create.mutate(
      { name: `Nova ekipa ${n}` },
      { onSuccess: (created) => openTeam(created.id) },
    );
  };

  const newPerson = () => {
    const names = new Set((volunteers.data ?? []).map((v) => v.name));
    let n = 1;
    while (names.has(`Nov prostovoljec ${n}`)) n += 1;
    mut.create.mutate(
      { name: `Nov prostovoljec ${n}`, email: null },
      { onSuccess: (created) => openPerson(created.id) },
    );
  };

  if (
    volunteers.isPending ||
    event.isPending ||
    posts.isPending ||
    shifts.isPending ||
    groups.isPending
  ) {
    return (
      <Center mih="60vh">
        <Loader />
      </Center>
    );
  }

  if (view === 'ekipe') {
    const teamRows = (groups.data ?? []).map((g) => ({
      group: g,
      minutes: g.slots.reduce(
        (a, sl) => a + rollupFor(sl, shifts.data ?? [], days, postById).minutes,
        0,
      ),
    }));
    const current = teamRows.find((t) => t.group.id === groupId) ?? null;
    return (
      <div
        style={{ display: 'flex', minHeight: 0, height: 'calc(100vh - 58px)', position: 'relative' }}
      >
        <TeamsSidebar
          rows={teamRows}
          search={teamSearch}
          setSearch={setTeamSearch}
          groupId={groupId}
          pick={openTeam}
          peopleCount={rows.length}
          setView={setView}
          newTeam={newTeam}
        />
        <div
          style={{ flex: 1, overflow: 'auto', minWidth: 0, background: 'oklch(0.978 0.004 95)' }}
        >
          {current ? (
            <TeamDetail
              key={current.group.id}
              group={current.group}
              days={days}
              shifts={shifts.data ?? []}
              postById={postById}
              onDeleted={() => openTeam(null)}
            />
          ) : (
            <TeamsEmpty hasTeams={teamRows.length > 0} onAdd={canPlan ? newTeam : undefined} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: 0, height: 'calc(100vh - 58px)', position: 'relative' }}>
      {tagMgrOpen ? (
        <TagManager
          kind="volunteer"
          tags={tags.data ?? []}
          usage={tagUsage}
          onClose={() => setTagMgrOpen(false)}
        />
      ) : null}

      <Sidebar
        setView={setView}
        teamCount={(groups.data ?? []).length}
        rows={rows}
        shown={shown}
        out={outRows}
        outShown={outShown}
        tags={tags.data ?? []}
        tagUsage={tagUsage}
        search={search}
        setSearch={setSearch}
        active={active}
        toggleFilter={(f) =>
          setActive((prev) => {
            const next = new Set(prev);
            if (next.has(f)) next.delete(f);
            else next.add(f);
            return next;
          })
        }
        tagFilter={tagFilter}
        toggleTagFilter={(id) =>
          setTagFilter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
        }
        clearTagFilter={() => setTagFilter([])}
        personId={personId}
        pick={(id) => openPerson(id)}
        selectMode={selectMode}
        toggleSelectMode={() => {
          setSelectMode((m) => !m);
          setSelected(new Set());
        }}
        selected={selected}
        setSelected={setSelected}
        openTagManager={() => setTagMgrOpen(true)}
        newPerson={newPerson}
        openImport={() => setModal('import')}
        openTriage={() => setModal('triage')}
        triageCount={triage.data?.length ?? 0}
      />

      <div style={{ flex: 1, overflow: 'auto', minWidth: 0, background: 'oklch(0.978 0.004 95)' }}>
        {current ? (
          <PersonDetail
            key={current.v.id}
            volunteer={current.v}
            rollup={current.r}
            tags={tags.data ?? []}
            days={days}
            tab={tab}
            setTab={(t) => openPerson(current.v.id, t)}
            onDeleted={() => openPerson(null)}
          />
        ) : (
          <EmptyState rows={allRows} openPerson={(id, t) => openPerson(id, t ?? 'profil')} />
        )}
      </div>

      <ImportModal opened={modal === 'import'} onClose={() => setModal('none')} />
      <TriageModal
        opened={modal === 'triage'}
        volunteers={volunteers.data ?? []}
        onClose={() => setModal('none')}
      />
    </div>
  );
}

/**
 * An empty screen is an invitation, not a mood: it says what a team is for here
 * and offers the one action that starts it.
 */
function TeamsEmpty({ hasTeams, onAdd }: { hasTeams: boolean; onAdd?: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        minHeight: '60vh',
        padding: '40px 28px',
        textAlign: 'center',
      }}
    >
      <div style={{ display: 'flex', gap: 5, marginBottom: 4 }}>
        <span style={{ width: 9, height: 9, borderRadius: 1, background: 'oklch(0.86 0.008 95)' }} />
        <span style={{ width: 9, height: 9, borderRadius: 1, background: 'oklch(0.9 0.006 95)' }} />
        <span style={{ width: 9, height: 9, borderRadius: 1, background: 'oklch(0.93 0.006 95)' }} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'oklch(0.3 0.012 90)' }}>
        {hasTeams ? 'Izberi ekipo.' : 'Zunanjih ekip še ni.'}
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: 'oklch(0.52 0.012 90)',
          lineHeight: 1.55,
          maxWidth: 380,
        }}
      >
        {hasTeams
          ? 'Na levi izberi ekipo, da vidiš njenega vodjo in kaj pokriva.'
          : 'Dodaj skupino, ki pokriva redarska mesta — PGD, GRS, klub ali organizacijsko ekipo. Njena mesta ne bodo več videti kot vrzel.'}
      </div>
      {hasTeams || !onAdd ? null : (
        <button
          type="button"
          onClick={onAdd}
          style={{
            marginTop: 8,
            cursor: 'pointer',
            padding: '8px 16px',
            borderRadius: 7,
            border: '1px solid oklch(0.85 0.05 245)',
            background: 'oklch(0.965 0.025 245)',
            color: 'oklch(0.4 0.08 245)',
            fontSize: 13.5,
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          Dodaj ekipo
        </button>
      )}
    </div>
  );
}
